import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [ageError, setAgeError] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [loadingTerms, setLoadingTerms] = useState(true);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    display_name: '',
    role_id: '',
    date_of_birth: '',
    accept_terms: false,
  });

  useEffect(() => {
    fetchRoles();
    fetchTerms();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/lookup/roles');
      const data = await response.json();

      if (data.success && data.data) {
        setRoles(data.data);
        // Set default to first role in sort order
        if (data.data.length > 0) {
          setFormData(prev => ({ ...prev, role_id: data.data[0].id.toString() }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await fetch('/api/cms/page/terms-and-conditions');
      const data = await response.json();

      if (data.success && data.paragraphs) {
        // Combine all paragraph content
        const content = data.paragraphs
          .map(p => {
            let html = '';
            if (p.heading) {
              html += `<h3 class="font-semibold mb-2">${p.heading}</h3>`;
            }
            if (p.content) {
              html += p.content;
            }
            return html;
          })
          .join('');
        setTermsContent(content);
      }
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      setTermsContent('<p>Failed to load Terms & Conditions. Please try again later.</p>');
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'date_of_birth') {
      // Validate age when date of birth changes
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 16) {
        setAgeError('You must be at least 16 years old to register');
      } else {
        setAgeError('');
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      return;
    }

    if (!formData.accept_terms) {
      return;
    }

    if (ageError) {
      return;
    }

    const result = await register(formData);

    if (result.success) {
      // If verification is required, go to pending page
      // Otherwise go to dashboard
      navigate('/verification-pending');
    }
  };

  return (
    <div className="card">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Register</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
            Display Name
          </label>
          <input
            type="text"
            id="display_name"
            name="display_name"
            value={formData.display_name}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="role_id" className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            id="role_id"
            name="role_id"
            value={formData.role_id}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading || loadingRoles}
          >
            {loadingRoles ? (
              <option value="">Loading roles...</option>
            ) : (
              <>
                <option value="">Select a role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        <div>
          <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
            Date of Birth
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading}
            max={new Date().toISOString().split('T')[0]}
          />
          {ageError && (
            <p className="text-red-500 text-sm mt-1">{ageError}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading}
            minLength="8"
          />
        </div>

        <div>
          <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <input
            type="password"
            id="password_confirmation"
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={handleChange}
            className="input-field"
            required
            disabled={isLoading}
          />
          {formData.password !== formData.password_confirmation && formData.password_confirmation && (
            <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Terms & Conditions
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 h-60 overflow-y-auto bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
            {loadingTerms ? (
              <div className="flex items-center justify-center h-full">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: termsContent }} />
            )}
          </div>

          <div className="mt-3 flex items-start">
            <input
              type="checkbox"
              id="accept_terms"
              name="accept_terms"
              checked={formData.accept_terms}
              onChange={handleChange}
              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              required
              disabled={isLoading}
            />
            <label htmlFor="accept_terms" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              I have read and accept the Terms & Conditions <span className="text-red-500">*</span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          className="w-full btn-primary"
          disabled={isLoading || formData.password !== formData.password_confirmation || !formData.accept_terms || ageError}
        >
          {isLoading ? 'Creating account...' : 'Register'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
